// MusicXML parser -> MusicXMLScore (spec §4.2.3). Front-end only, in-memory (NFR-5).

import type { MusicXMLScore, Part, Measure, NoteEvent, ChordEvent, ScoreImportResult } from '../types';

const STEP_TO_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const CHORD_KIND: Record<string, string> = {
  major: '', minor: 'm', augmented: 'aug', diminished: 'dim',
  dominant: '7', 'major-seventh': 'maj7', 'minor-seventh': 'm7',
  'suspended-fourth': 'sus4', 'suspended-second': 'sus2',
};

export function parseMusicXML(xmlText: string): ScoreImportResult {
  const errors: string[] = [];
  const unsupported: string[] = [];
  try {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) {
      return { success: false, errors: ['XML 解析失败：文件不是合法的 MusicXML'], unsupportedElements: [] };
    }
    const scoreEl = doc.documentElement;
    const title = text(scoreEl, 'work-title') || text(scoreEl, 'movement-title') || '未命名乐谱';
    const composer = text(scoreEl, 'creator') || undefined;

    // Global defaults (first part's first measure).
    let divisions = 1;
    let timeSig: [number, number] = [4, 4];
    let keySig = 0;
    let bpm = 120; // E-7 default

    const partList = Array.from(scoreEl.getElementsByTagName('part'));
    const parts: Part[] = [];
    const chords: ChordEvent[] = [];

    for (const partEl of partList) {
      const partId = partEl.getAttribute('id') ?? `P${parts.length}`;
      const partNameEl = scoreEl.querySelector(`part-list > score-part[id="${partId}"] > part-name`);
      const name = partNameEl?.textContent?.trim() || `Part ${partId}`;
      const measures: Measure[] = [];
      const measureEls = Array.from(partEl.getElementsByTagName('measure'));
      for (const mEl of measureEls) {
        const mnum = Number(mEl.getAttribute('number') ?? measures.length + 1);
        // Attributes (divisions/time/key) may appear here.
        const attr = mEl.getElementsByTagName('attributes')[0];
        if (attr) {
          const divEl = attr.getElementsByTagName('divisions')[0];
          if (divEl) divisions = Math.max(1, Number(divEl.textContent) || 1);
          const time = attr.getElementsByTagName('time')[0];
          if (time) {
            const beats = time.getElementsByTagName('beats')[0]?.textContent;
            const bt = time.getElementsByTagName('beat-type')[0]?.textContent;
            if (beats && bt) timeSig = [Number(beats), Number(bt)];
          }
          const key = attr.getElementsByTagName('key')[0];
          if (key) {
            const fifths = key.getElementsByTagName('fifths')[0]?.textContent;
            if (fifths) keySig = Number(fifths);
          }
        }
        const sound = mEl.getElementsByTagName('sound')[0];
        if (sound?.getAttribute('tempo')) bpm = Number(sound.getAttribute('tempo')) || bpm;

        // Harmony / chord symbols in this measure.
        const harmonies = mEl.getElementsByTagName('harmony');
        for (const h of harmonies) {
          const root = h.getElementsByTagName('root-step')[0]?.textContent?.trim() ?? '';
          const kind = h.getElementsByTagName('kind')[0]?.getAttribute('text') || h.getElementsByTagName('kind')[0]?.textContent || '';
          const bass = h.getElementsByTagName('bass-step')[0]?.textContent?.trim() ?? '';
          const suffix = CHORD_KIND[kind] ?? kind;
          chords.push({ measure: mnum, chordName: `${root}${suffix}${bass ? '/' + bass : ''}` });
        }

        const notes = parseNotes(mEl, divisions);
        measures.push({ number: mnum, notes });
      }
      parts.push({ id: partId, name, measures });
    }

    if (parts.length === 0) {
      return { success: false, errors: ['未找到任何声部 (<part>)'], unsupportedElements: unsupported };
    }

    const score: MusicXMLScore = { title, composer, parts, timeSignature: timeSig, keySignature: keySig, bpm, chords };
    return { success: true, score, errors, unsupportedElements: unsupported };
  } catch (e) {
    errors.push(String(e));
    return { success: false, errors, unsupportedElements: [] };
  }
}

function parseNotes(mEl: Element, divisions: number): NoteEvent[] {
  const notes: NoteEvent[] = [];
  let cursor = 0; // beats within measure
  let lastOnset = 0; // start time of most recent note (for <chord/> stacking)
  for (const child of Array.from(mEl.children)) {
    if (child.tagName === 'backup') {
      const d = Number(child.getElementsByTagName('duration')[0]?.textContent) || 0;
      cursor -= d / divisions;
      continue;
    }
    if (child.tagName === 'forward') {
      const d = Number(child.getElementsByTagName('duration')[0]?.textContent) || 0;
      cursor += d / divisions;
      lastOnset = cursor;
      continue;
    }
    if (child.tagName === 'harmony' || child.tagName === 'direction' || child.tagName === 'attributes') continue;
    if (child.tagName !== 'note') continue;

    const isChord = !!child.getElementsByTagName('chord')[0];
    const durEl = child.getElementsByTagName('duration')[0];
    const dur = durEl ? Number(durEl.textContent) / divisions : 0;
    const rest = child.getElementsByTagName('rest')[0];
    const pitchEl = child.getElementsByTagName('pitch')[0];
    const id = child.getAttribute('id') ?? `n${notes.length}`;

    if (!pitchEl || rest) {
      if (!isChord) { cursor += dur; lastOnset = cursor; }
      continue;
    }
    const step = pitchEl.getElementsByTagName('step')[0]?.textContent?.trim() ?? 'C';
    const octave = Number(pitchEl.getElementsByTagName('octave')[0]?.textContent ?? 4);
    const alter = Number(pitchEl.getElementsByTagName('alter')[0]?.textContent ?? 0);
    const midi = (octave + 1) * 12 + (STEP_TO_PC[step] ?? 0) + alter;

    const onset = isChord ? lastOnset : cursor;
    notes.push({
      id,
      pitch: midi,
      startTime: onset,
      duration: dur,
      velocity: 1,
      slur: !!child.getElementsByTagName('slur')[0],
      upBow: !!child.querySelector('technical > up-bow'),
      downBow: !!child.querySelector('technical > down-bow'),
    });
    lastOnset = onset;
    if (!isChord) cursor += dur;
  }
  return notes;
}

function text(root: Element, tag: string): string {
  return root.getElementsByTagName(tag)[0]?.textContent?.trim() ?? '';
}

// Flatten a score into a single ordered timeline of notes (first part) for follow mode.
export function flattenTimeline(score: MusicXMLScore): { note: NoteEvent; measure: number }[] {
  const out: { note: NoteEvent; measure: number }[] = [];
  const part = score.parts[0];
  if (!part) return out;
  let measureOffset = 0;
  const beatsPerMeasure = score.timeSignature[0];
  for (const m of part.measures) {
    for (const n of m.notes) {
      out.push({ note: { ...n, startTime: n.startTime + measureOffset }, measure: m.number });
    }
    measureOffset += beatsPerMeasure;
  }
  return out;
}
